import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Mail } from "lucide-react";
import ContactForm from "@/pages/marketing/ContactForm";

const IMG_ABOUT =
  "https://images.unsplash.com/photo-1515378960530-7c0da6231fb1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTJ8MHwxfHNlYXJjaHwzfHxwcm9mZXNzaW9uYWwlMjB1c2luZyUyMGxhcHRvcHxlbnwwfHx8fDE3ODc3Mzg4OTh8MA&ixlib=rb-4.1.0&q=85";

export default function About() {
  const location = useLocation();

  useEffect(() => {
    if (location.hash === "#contact") {
      const el = document.getElementById("contact");
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 80);
    }
  }, [location.hash]);

  return (
    <div data-testid="marketing-about-page">
      <section className="max-w-6xl mx-auto px-6 pt-20 pb-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <motion.h1
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="text-4xl sm:text-5xl font-black text-slate-900 tracking-tighter"
              style={{ fontFamily: "Outfit" }}
              data-testid="about-page-title"
            >
              Built From Real-World Content Chaos.
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="text-slate-600 text-base md:text-lg mt-6 leading-relaxed"
              data-testid="about-page-content"
            >
              Post Your Content (PYC) started the way most genuinely useful tools do — solving a real,
              messy problem for a real business that needed a consistent, high-quality social presence
              without sacrificing accuracy or oversight. We realized businesses needed the power of AI,
              but with the safety of human approval. That's why we built PYC — every draft, AI-made or
              not, still needs a human's yes before it goes live.
            </motion.p>
          </div>
          <motion.div
            initial={{ opacity: 0, x: 24 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="hidden lg:block lg:col-span-5"
          >
            <img src={IMG_ABOUT} alt="" className="w-full h-72 object-cover rounded-3xl" />
          </motion.div>
        </div>
      </section>

      <section id="contact" className="max-w-6xl mx-auto px-6 py-20">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 tracking-tight mb-4" style={{ fontFamily: "Outfit" }}>
              Contact Us
            </h2>
            <p className="text-slate-600 leading-relaxed mb-6">
              Tell us a bit about your team and we'll set up a personalized walkthrough — no
              self-serve sign-up, just a real conversation about your workflow.
            </p>
            <a
              href="mailto:hello@postyourcontent.com"
              className="inline-flex items-center gap-2 text-slate-900 font-semibold text-sm hover:text-slate-600 transition-colors"
              data-testid="about-contact-email-link"
            >
              <Mail size={16} /> hello@postyourcontent.com
            </a>
          </div>
          <div className="md:col-span-7">
            <div className="rounded-3xl border border-slate-200 bg-white p-8 shadow-sm">
              <ContactForm />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
